import {
  TranscriptElement,
  TranscriptTextElement,
} from '../../types.ts/transcript';
import { VideoElement } from './elements/VideoElement';
import { Timeline } from './Timeline';

/**
 * Extended transcript element with status for full transcript inference
 */
export type InferredTranscriptElement = TranscriptElement & {
  status?: 'included' | 'removed' | string; /// Temp fix for TS error on return of inferrredFullTranscript
};

/**
 * InferredTranscript class that handles the inference of transcript content based on timeline
 */
export class InferredTranscript {
  /**
   * The original transcript elements reference
   */
  private originalTranscription: ReadonlyArray<TranscriptElement>;

  /**
   * Creates a new InferredTranscript instance
   * @param originalTranscription The original transcript elements
   */
  constructor(originalTranscription: ReadonlyArray<TranscriptElement>) {
    this.originalTranscription = originalTranscription;
  }

  /**
   * Infers a full transcript based on a timeline, showing which parts of the original transcript
   * are included or removed in the current edit
   *
   * This algorithm identifies specific patterns in the timeline segments:
   * - If there are text elements between two timeline segments that aren't directly sequential in the
   *   source material (e.g., A and C segments with B text in between), this function will mark those
   *   "B" elements as included.
   * - If segments are sequential in the source material (directly adjacent in timestamps), or if they're
   *   not in original order (e.g., A->E->B), or if there are no text elements between consecutive segments,
   *   no inference is performed and those elements remain marked as "removed".
   *
   * @param timeline The timeline containing video elements
   * @returns Array of transcript elements with status property indicating if each element
   * is included in the timeline (status='included') or was removed (status='removed')
   */
  inferFullTranscript(timeline: Timeline): InferredTranscriptElement[] {
    // 1. Get all video elements with transcript segments
    const videoElements = timeline.allElements.filter(
      (el) => el.type === 'video',
    ) as VideoElement[];

    // 2. Sort by timeline position
    const sortedElements = videoElements.sort((a, b) => a.time - b.time);

    // 3. Create a map to track which parts of the original transcript are included
    const originalTranscription = Array.from(this.originalTranscription);
    const includedInitialIndexes = new Set<number>();

    // 4. Collect all included initial indexes from timeline elements
    sortedElements.forEach((el) => {
      const segment = el.getTranscriptSegment();
      if (segment) {
        const mediaStartTime = el.getMediaStartTime();
        const mediaEndTime = el.getMediaEndTime();

        // Get all original transcript words and check if they fall within this segment's time range
        originalTranscription.forEach((word) => {
          if (word.type === 'text') {
            const textElement = word as TranscriptTextElement;
            // Check if this word's time range overlaps with the segment's time range
            if (
              textElement.ts !== undefined &&
              textElement.end_ts !== undefined &&
              textElement.ts >= mediaStartTime &&
              textElement.end_ts <= mediaEndTime
            ) {
              includedInitialIndexes.add(word.initial_index);
            }
          } else if (word.type === 'punct') {
            // For punctuation, include it if it's between two included words
            // We'll handle this in a subsequent pass
            const punctIndex = originalTranscription.indexOf(word);

            if (
              punctIndex > 0 &&
              punctIndex < originalTranscription.length - 1
            ) {
              const prevElement = originalTranscription[punctIndex - 1];
              const nextElement = originalTranscription[punctIndex + 1];

              if (
                prevElement.type === 'text' &&
                nextElement.type === 'text' &&
                includedInitialIndexes.has(prevElement.initial_index) &&
                includedInitialIndexes.has(nextElement.initial_index)
              ) {
                includedInitialIndexes.add(word.initial_index);
              }
            }
          }
        });
      }
    });

    // 5. To infer gaps we **must** look at clips in the order they appear in
    // the *source media*, not necessarily their order on the timeline.  Users
    // can place clips out-of-order (e.g. a flash-back) and we must avoid
    // inferring a gap that will be filled by a later-in-timeline but earlier-
    // in-media clip.  Therefore create a list sorted by trim_start (media
    // position) and iterate through adjacent pairs in that order.

    // Build array sorted by media order (trim_start) so that we only ever
    // infer gaps between clips that appear sequentially in the *source*.
    const mediaSortedElements = [...sortedElements].sort(
      (a, b) => (a.trim_start ?? 0) - (b.trim_start ?? 0),
    );

    for (let i = 0; i < mediaSortedElements.length - 1; i++) {
      const currentEl = mediaSortedElements[i];
      const nextEl = mediaSortedElements[i + 1];

      const currentSegment = currentEl.getTranscriptSegment();
      const nextSegment = nextEl.getTranscriptSegment();

      if (!currentSegment || !nextSegment) continue;

      // Ensure clips also maintain forward order *on the timeline*.  This
      // matches UX spec: we only infer when the editor arranges clips in the
      // same order they appear in the source media.
      if (nextEl.time <= currentEl.time) {
        continue;
      }

      // Get all words from each segment and find text elements
      const currentWords = currentSegment
        .getWords()
        .filter((w) => w.type === 'text');
      const nextWords = nextSegment.getWords().filter((w) => w.type === 'text');

      // If either segment has no text words, skip
      if (currentWords.length === 0 || nextWords.length === 0) continue;

      // Find the last text word in current segment and first text word in next segment
      const lastWord = currentWords[
        currentWords.length - 1
      ] as TranscriptTextElement;
      const firstWord = nextWords[0] as TranscriptTextElement;

      // Check if these words are far apart in the original transcript (skipping words)
      // but sequential in the source material (one follows the other in timestamp order)
      const isSequentialInSource =
        Math.abs(lastWord.end_ts - firstWord.ts) < 0.05; // 50ms threshold

      if (
        !isSequentialInSource &&
        this.areWordsConsecutiveInOriginal(
          lastWord as TranscriptTextElement,
          firstWord as TranscriptTextElement,
          originalTranscription,
        )
      ) {
        // Find and mark all words between them in the original transcript
        this.addWordsBetween(
          lastWord as TranscriptTextElement,
          firstWord as TranscriptTextElement,
          originalTranscription,
          includedInitialIndexes,
        );
      }
    }

    // Special case: If we have one video element that covers the entire transcript
    // and no words are detected as "included", this means the video element
    // likely fully contains the transcript. In this case, we mark all words as included.
    if (videoElements.length === 1 && includedInitialIndexes.size === 0) {
      const singleElement = videoElements[0];
      const segment = singleElement.getTranscriptSegment();

      if (segment) {
        // Check if this segment's time range covers a substantial portion of the transcript
        const firstTextElement = originalTranscription.find(
          (el) => el.type === 'text',
        ) as TranscriptTextElement;
        const lastTextElement = [...originalTranscription]
          .reverse()
          .find((el) => el.type === 'text') as TranscriptTextElement;

        if (
          firstTextElement &&
          lastTextElement &&
          segment.start <= firstTextElement.ts &&
          segment.end >= lastTextElement.end_ts
        ) {
          // This video element covers the entire transcript - mark all as included
          console.log(
            'Single video element covers entire transcript, marking all words as included',
          );
          originalTranscription.forEach((element) => {
            includedInitialIndexes.add(element.initial_index);
          });
        }
      }
    }

    // If no words were inferred as included and the timeline is empty we treat all
    // transcript words as "removed" so that downstream logic can distinguish
    // between words that are present on the timeline and those that are not. We
    // therefore purposefully **do not** apply the previous fallback that marked
    // everything as included – that behaviour masked genuine empty-timeline
    // scenarios and broke tests that rely on the distinction.

    // 6. Create a full transcript with status indicators
    const result = originalTranscription.map((element) => {
      const isIncluded = includedInitialIndexes.has(element.initial_index);
      return {
        ...element,
        status: isIncluded ? 'included' : 'removed',
      };
    });

    console.log(
      `InferredTranscript: Created inferred transcript with ${result.length} elements, ${includedInitialIndexes.size} included`,
    );
    return result;
  }

  /**
   * Checks if there are text elements between two words in the original transcript
   *
   * @param word1 The first word
   * @param word2 The second word
   * @param originalTranscription The original transcript elements
   * @returns Boolean indicating if there are text elements between the words that should be inferred
   * @private Used by inferFullTranscript
   */
  private areWordsConsecutiveInOriginal(
    word1: TranscriptTextElement,
    word2: TranscriptTextElement,
    originalTranscription: TranscriptElement[],
  ): boolean {
    // Find the indices of both words in the original transcript
    const index1 = originalTranscription.findIndex(
      (w) => w.initial_index === word1.initial_index,
    );

    const index2 = originalTranscription.findIndex(
      (w) => w.initial_index === word2.initial_index,
    );

    if (index1 === -1 || index2 === -1 || index1 >= index2) return false;

    // Check for any text elements between the two words
    for (let i = index1 + 1; i < index2; i++) {
      if (originalTranscription[i].type === 'text') {
        // Found a text element between our words, this means we need to include those intermediate words
        return true;
      }
    }

    // No text elements found between the words, so there's nothing to infer
    return false;
  }

  /**
   * Adds all words between two words to the included set
   *
   * @param word1 The first word
   * @param word2 The second word
   * @param originalTranscription The original transcript elements
   * @param includedInitialIndexes Set of included initial indexes to update
   * @private Used by inferFullTranscript
   */
  private addWordsBetween(
    word1: TranscriptTextElement,
    word2: TranscriptTextElement,
    originalTranscription: TranscriptElement[],
    includedInitialIndexes: Set<number>,
  ): void {
    // Find the indices of both words in the original transcript
    const index1 = originalTranscription.findIndex(
      (w) => w.initial_index === word1.initial_index,
    );

    const index2 = originalTranscription.findIndex(
      (w) => w.initial_index === word2.initial_index,
    );

    if (index1 === -1 || index2 === -1 || index1 >= index2) return;

    // Add all initial indexes between the two indices
    for (let i = index1 + 1; i < index2; i++) {
      const element = originalTranscription[i];
      includedInitialIndexes.add(element.initial_index);
    }
  }
}
